import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tabs, Table, Button, Space, Select, Tooltip, message, Pagination, Spin, Modal, Input, AutoComplete, Empty, Popconfirm } from 'antd';
import { pinyin } from 'pinyin-pro';
import {
  ReloadOutlined,
  RadarChartOutlined,
  BulbOutlined,
  TeamOutlined,
  ApartmentOutlined,
  DatabaseOutlined,
  CheckOutlined,
  StopOutlined,
  RedoOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  FieldTimeOutlined,
  ShareAltOutlined,
  UnorderedListOutlined,
  QuestionCircleOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  UserOutlined,
  FileSearchOutlined,
  SearchOutlined,
  EditOutlined,
  SaveOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  workIntelApi,
  PERSONA_MIN_MSGS,
  type WorkIntelTodo,
  type WorkIntelKnowledge,
  type WorkIntelContact,
  type WorkIntelRelation,
  type WorkIntelBatch,
  type WorkIntelTraceMsg,
  type AudienceProfile,
} from '../../services/workIntelApi';
import './work-intel.css';

// 关系图谱按需懒加载:echarts 只在进入图谱视图时才下载,不进主包
const RelationGraph = lazy(() => import('./RelationGraph'));

const fmtTime = (v?: string | null, format = 'MM-DD HH:mm') => (v ? dayjs(v).format(format) : '-');

const urgencyPill = (u: string) => {
  const map: Record<string, { cls: string; text: string }> = {
    high: { cls: 'wi-pill wi-pill-high', text: '紧急' },
    medium: { cls: 'wi-pill wi-pill-medium', text: '中等' },
    low: { cls: 'wi-pill wi-pill-low', text: '低' },
  };
  const m = map[u] || map.medium;
  return <span className={m.cls}>{m.text}</span>;
};

/**
 * 待办「相关联系人」标签行:从该待办的原始对话说话人反查而来(本人特别标记)。
 * onPick 传入时点击可跳到该联系人(有 contactId 才可点)。
 */
function RelatedContacts({
  list,
  onPick,
}: {
  list?: WorkIntelTodo['relatedContacts'];
  onPick?: (contactId: string) => void;
}) {
  if (!list || list.length === 0) return null;
  return (
    <div className="wi-todo-related">
      <TeamOutlined style={{ fontSize: 12, opacity: 0.7 }} />
      <span className="wi-todo-related-lbl">相关</span>
      {list.map((p, i) =>
        p.isSelf ? (
          <span key={i} className="wi-related-chip wi-related-self">本人</span>
        ) : p.contactId && onPick ? (
          <button
            key={i}
            type="button"
            className="wi-related-chip wi-related-link"
            onClick={() => onPick(p.contactId!)}
          >
            {p.name}
          </button>
        ) : (
          <span key={i} className="wi-related-chip">{p.name}</span>
        ),
      )}
    </div>
  );
}

function EmptyGuide({ title, sub }: { title: string; sub: React.ReactNode }) {
  return (
    <div className="wi-empty">
      <div className="wi-empty-icon"><RadarChartOutlined /></div>
      <div className="wi-empty-title">{title}</div>
      <div className="wi-empty-sub">{sub}</div>
    </div>
  );
}

const PIPELINE_HINT = (
  <>
    数据链路:微信采集器(开启<b>智能研判</b>通道)上传 → 攒批(约25条或30分钟)→ <b>AI 研判提炼</b> → 沉淀到这里。
    <br />
    保持采集器登录并监控,有新消息后稍等片刻即可看到产物。
  </>
);

// ===== 待办溯源弹窗:展示该事项依据的原始聊天消息(领导要看的"依据") =====
function TraceModal({ todo, onClose }: { todo: WorkIntelTodo | null; onClose: () => void }) {
  const [trace, setTrace] = useState<{ legacy: boolean; messages: WorkIntelTraceMsg[] } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!todo) { setTrace(null); return; }
    setLoading(true);
    workIntelApi
      .todoTrace(todo.id)
      .then((r) => {
        if (r?.ok) setTrace({ legacy: !!r.legacy, messages: r.messages || [] });
        else message.error(r?.reason || '溯源加载失败');
      })
      .catch(() => message.error('溯源加载失败'))
      .finally(() => setLoading(false));
  }, [todo]);

  // 按会话分组展示(同一事项的依据消息可能来自多个聊天)
  const convs = useMemo(() => {
    const m = new Map<string, { talker: string; talkerName: string; isGroup: boolean; msgs: WorkIntelTraceMsg[] }>();
    for (const msg of trace?.messages || []) {
      if (!m.has(msg.talker)) m.set(msg.talker, { talker: msg.talker, talkerName: msg.talkerName, isGroup: msg.isGroup, msgs: [] });
      m.get(msg.talker)!.msgs.push(msg);
    }
    return Array.from(m.values());
  }, [trace]);

  return (
    <Modal
      open={!!todo}
      onCancel={onClose}
      footer={null}
      width={680}
      className="wi-trace-modal"
      title={<span><FileSearchOutlined style={{ marginRight: 8, color: '#22d3ee' }} />事项详情 · 原始聊天溯源</span>}
    >
      {todo && (
        <>
          <div className="wi-trace-todo">
            <div className="wi-trace-todo-head">{urgencyPill(todo.urgency)}<b>{todo.content}</b></div>
            <div className="wi-todo-meta">
              {todo.owner && <span>责任人 <b>{todo.owner}</b></span>}
              {todo.dueAt && <span><FieldTimeOutlined /> 截止 {fmtTime(todo.dueAt, 'YYYY-MM-DD')}</span>}
              <span>AI 研判于 {fmtTime(todo.createdAt, 'YYYY-MM-DD HH:mm')}</span>
            </div>
            <RelatedContacts list={todo.relatedContacts} />
          </div>
          {loading ? (
            <div className="wi-trace-loading"><Spin /> 正在回溯原始聊天…</div>
          ) : trace?.legacy ? (
            <div className="wi-trace-legacy">
              该事项产生于「聊天溯源」功能上线之前的历史研判,当时未记录依据消息,无法精确回溯。
              <br />此后新研判出的事项,都能在这里直接看到它依据的原始聊天记录。
            </div>
          ) : (
            <div className="wi-trace-msgs">
              {convs.map((cv) => (
                <div key={cv.talker} className="wi-trace-conv">
                  <div className="wi-trace-conv-head">
                    {cv.isGroup ? '👥 群聊' : '💬 私聊'} · {cv.talkerName}
                  </div>
                  {cv.msgs.map((msg) => (
                    <div key={msg.msgId} className={`wi-trace-msg${msg.isSelf ? ' wi-trace-msg-self' : ''}`}>
                      <span className="wi-trace-msg-time">{msg.time ? dayjs(msg.time).format('MM-DD HH:mm') : '--'}</span>
                      <span className="wi-trace-msg-sender">{msg.isSelf ? '我' : msg.sender || '?'}</span>
                      <span className="wi-trace-msg-text">{msg.content}</span>
                    </div>
                  ))}
                </div>
              ))}
              {convs.length === 0 && (
                <div className="wi-trace-legacy">依据消息未能在原始消息库中找到(可能已超出留存范围)。</div>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

// ===== 待办雷达 =====
function RadarPanel({ onChanged }: { onChanged: () => void }) {
  const [radar, setRadar] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  // 在办雷达 / 已办结历史 双视图
  const [view, setView] = useState<'active' | 'history'>('active');
  // 我的待办 / 他人负责 分栏:他人的事只需知悉跟踪,不给"开始/完成"按钮
  const [scope, setScope] = useState<'mine' | 'others'>('mine');
  const [history, setHistory] = useState<WorkIntelTodo[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // 正在提交的待办(id → 目标状态):按钮立刻进入 loading,消除"点了没反应"
  const [acting, setActing] = useState<Record<string, string>>({});
  // 溯源弹窗:当前查看详情的待办
  const [tracing, setTracing] = useState<WorkIntelTodo | null>(null);
  // 分页:每个紧急度分组独立翻页 + 历史翻页(不再无限下拉)
  const SEC_SIZE = 8;
  const [secPage, setSecPage] = useState<Record<string, number>>({});
  const [histPage, setHistPage] = useState(1);

  // 本人标识集合(后端从 raw 的 is_self 计算 + 研判契约"我",通用机制不写死):
  // owner 为空或命中本人标识 → 我的待办;否则 → 他人负责
  const selfSet = useMemo(() => new Set<string>(radar?.meta?.selfIds || ['我']), [radar]);
  const isMine = useCallback(
    (t: WorkIntelTodo) => {
      const owner = (t.owner || '').trim();
      return !owner || selfSet.has(owner);
    },
    [selfSet],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRadar(await workIntelApi.radar());
    } catch {
      message.error('待办雷达加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const [done, ignored] = await Promise.all([
        workIntelApi.todos(1, 100, 'done'),
        workIntelApi.todos(1, 100, 'ignored'),
      ]);
      const merged: WorkIntelTodo[] = [...(done?.items || []), ...(ignored?.items || [])];
      merged.sort((a, b) => dayjs(b.updatedAt || b.createdAt).valueOf() - dayjs(a.updatedAt || a.createdAt).valueOf());
      setHistory(merged);
    } catch {
      message.error('历史记录加载失败');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (view === 'history') loadHistory(); }, [view, loadHistory]);
  // 切换 我的/他人 或 在办/历史 时,分组页码归位
  useEffect(() => { setSecPage({}); setHistPage(1); }, [scope, view]);

  /** 乐观更新:先把这条待办从本地雷达里挪走/改状态,界面立即响应,API 在后台确认;失败则回滚重载 */
  const act = async (id: string, status: string) => {
    setActing((m) => ({ ...m, [id]: status }));
    const before = radar;
    if (radar) {
      const next = { ...radar };
      for (const k of ['overdue', 'dueToday', 'dueSoon', 'later', 'noDue']) {
        const list: WorkIntelTodo[] = next[k] || [];
        next[k] = status === 'doing'
          ? list.map((t) => (t.id === id ? { ...t, status: 'doing' } : t))
          : list.filter((t) => t.id !== id);
      }
      setRadar(next);
    }
    message.success(status === 'done' ? '已完成 ✓(可在"已办结历史"里查看)' : status === 'ignored' ? '已忽略' : '已开始,加油!');
    try {
      const r = await workIntelApi.updateTodoStatus(id, status);
      if (!r?.ok) throw new Error(r?.reason || '操作失败');
      onChanged();
    } catch (e: any) {
      message.error(`${e?.message || '操作失败'},已还原`);
      setRadar(before);
      load();
    } finally {
      setActing((m) => {
        const { [id]: _, ...rest } = m;
        return rest;
      });
    }
  };

  /** 历史记录恢复为在办 */
  const restore = async (id: string) => {
    setActing((m) => ({ ...m, [id]: 'open' }));
    try {
      const r = await workIntelApi.updateTodoStatus(id, 'open');
      if (!r?.ok) throw new Error(r?.reason || '操作失败');
      message.success('已恢复为在办');
      setHistory((h) => h.filter((t) => t.id !== id));
      load();
      onChanged();
    } catch (e: any) {
      message.error(e?.message || '操作失败');
    } finally {
      setActing((m) => {
        const { [id]: _, ...rest } = m;
        return rest;
      });
    }
  };

  const sections: Array<{ key: string; title: string; cls: string; hint?: string }> = [
    { key: 'overdue', title: '已逾期', cls: 'wi-red', hint: '截止时间已过还没办结的事项' },
    { key: 'dueToday', title: '今天到期', cls: 'wi-orange' },
    { key: 'dueSoon', title: '3 天内到期', cls: 'wi-gold' },
    { key: 'later', title: '更远排期', cls: 'wi-blue', hint: '截止时间在 3 天以后' },
    { key: 'noDue', title: '未排期(没提取到截止时间)', cls: 'wi-slate', hint: 'AI 从聊天里没找到明确的截止时间,这些事没有期限约束;点"开始/完成"正常管理即可' },
  ];
  const allOpen: WorkIntelTodo[] = radar ? sections.flatMap((sec) => (radar[sec.key] || []) as WorkIntelTodo[]) : [];
  const mineCount = allOpen.filter(isMine).length;
  const othersCount = allOpen.length - mineCount;
  const total = scope === 'mine' ? mineCount : othersCount;

  return (
    <div>
      <Space style={{ marginBottom: 14 }} wrap>
        <div className="wi-view-toggle">
          <button type="button" className={view === 'active' ? 'on' : ''} onClick={() => setView('active')}>
            <ThunderboltOutlined /> 在办雷达
          </button>
          <button type="button" className={view === 'history' ? 'on' : ''} onClick={() => setView('history')}>
            <CheckCircleOutlined /> 已办结历史
          </button>
        </div>
        {view === 'active' && (
          <div className="wi-view-toggle">
            <button type="button" className={scope === 'mine' ? 'on' : ''} onClick={() => setScope('mine')}>
              <UserOutlined /> 我的待办{radar ? ` ${mineCount}` : ''}
            </button>
            <button type="button" className={scope === 'others' ? 'on' : ''} onClick={() => setScope('others')}>
              <TeamOutlined /> 他人负责{radar ? ` ${othersCount}` : ''}
            </button>
          </div>
        )}
        <Button icon={<ReloadOutlined />} onClick={view === 'active' ? load : loadHistory} loading={view === 'active' ? loading : historyLoading}>刷新</Button>
        {view === 'active' && radar && (
          <span style={{ color: '#b8cdec', fontSize: 13.5 }}>
            扫描于 {fmtTime(radar.generatedAt, 'HH:mm:ss')} · {scope === 'mine' ? '我的在办' : '他人负责'} {total} 项
          </span>
        )}
      </Space>

      {view === 'active' && scope === 'others' && othersCount > 0 && (
        <div className="wi-others-hint">
          这些是<b>别人承诺或被交办</b>的事,AI 帮你盯着进展,不用你亲自办;确已完结或不想再跟踪,点「不再跟踪」移入历史。
        </div>
      )}

      {view === 'history' ? (
        history.length === 0 && !historyLoading ? (
          <EmptyGuide title="还没有办结记录" sub="在待办雷达里点「完成」或「忽略」的事项都会沉淀到这里,随时可回看、可恢复。" />
        ) : (
          <div className="wi-radar-sec wi-green">
            <div className="wi-radar-sec-head">
              <span className="wi-radar-dot" />
              已办结 / 已忽略
              <span className="wi-radar-count">{history.length}</span>
            </div>
            {history.slice((histPage - 1) * SEC_SIZE, histPage * SEC_SIZE).map((t) => (
              <div key={t.id} className="wi-todo-row">
                <span className={`wi-pill ${t.status === 'done' ? 'wi-pill-done' : 'wi-pill-low'}`}>
                  {t.status === 'done' ? '✓ 完成' : '已忽略'}
                </span>
                <div className="wi-todo-main">
                  <div className="wi-todo-content" style={{ opacity: 0.85 }}>{t.content}</div>
                  <div className="wi-todo-meta">
                    {t.owner && <span>责任人 <b>{t.owner}</b></span>}
                    {t.dueAt && <span><FieldTimeOutlined /> 原截止 {fmtTime(t.dueAt, 'YYYY-MM-DD')}</span>}
                    <span>办结于 {fmtTime(t.updatedAt || t.createdAt, 'YYYY-MM-DD HH:mm')}</span>
                  </div>
                  <RelatedContacts list={t.relatedContacts} />
                </div>
                <div className="wi-todo-actions">
                  <Tooltip title="查看详情与原始聊天依据">
                    <Button size="small" icon={<FileSearchOutlined />} onClick={() => setTracing(t)} />
                  </Tooltip>
                  <Button size="small" loading={acting[t.id] === 'open'} onClick={() => restore(t.id)}>恢复为在办</Button>
                </div>
              </div>
            ))}
            {history.length > SEC_SIZE && (
              <div style={{ textAlign: 'right', padding: '8px 14px' }}>
                <Pagination
                  size="small" current={histPage} pageSize={SEC_SIZE} total={history.length}
                  showSizeChanger={false} onChange={setHistPage}
                />
              </div>
            )}
          </div>
        )
      ) : (
        <>
          {radar && total === 0 && (
            <EmptyGuide
              title={scope === 'mine' ? '雷达无回波 —— 你没有在办事项' : '没有需要跟踪的他人事项'}
              sub={scope === 'mine' ? PIPELINE_HINT : '聊天里出现"别人承诺/被交办的事"时,AI 会提取到这里帮你盯进展。'}
            />
          )}
          {radar && sections.map((sec) => {
            const items: WorkIntelTodo[] = ((radar[sec.key] || []) as WorkIntelTodo[]).filter((t) =>
              scope === 'mine' ? isMine(t) : !isMine(t),
            );
            if (!items.length) return null;
            const pg = secPage[sec.key] || 1;
            const shown = items.slice((pg - 1) * SEC_SIZE, pg * SEC_SIZE);
            return (
              <div key={sec.key} className={`wi-radar-sec ${sec.cls}`}>
                <div className="wi-radar-sec-head">
                  <span className="wi-radar-dot" />
                  {sec.title}
                  {sec.hint && (
                    <Tooltip title={sec.hint}>
                      <QuestionCircleOutlined style={{ fontSize: 13, color: '#8fb0dd', cursor: 'help' }} />
                    </Tooltip>
                  )}
                  <span className="wi-radar-count">{items.length}</span>
                </div>
                {shown.map((t) => (
                  <div key={t.id} className="wi-todo-row">
                    {urgencyPill(t.urgency)}
                    <div className="wi-todo-main">
                      <div className="wi-todo-content">
                        {scope === 'others' && t.owner && <span className="wi-owner-tag">{t.owner}</span>}
                        {t.content}
                      </div>
                      <div className="wi-todo-meta">
                        {scope === 'mine' && t.owner && !selfSet.has((t.owner || '').trim()) && (
                          <span>责任人 <b>{t.owner}</b></span>
                        )}
                        {t.dueAt && <span><FieldTimeOutlined /> {fmtTime(t.dueAt, 'YYYY-MM-DD')}</span>}
                        {t.status === 'doing' && <span style={{ color: '#93c5fd' }}>进行中</span>}
                      </div>
                      <RelatedContacts list={t.relatedContacts} />
                    </div>
                    <div className="wi-todo-actions">
                      <Tooltip title="查看详情与原始聊天依据">
                        <Button size="small" icon={<FileSearchOutlined />} onClick={() => setTracing(t)}>溯源</Button>
                      </Tooltip>
                      {scope === 'mine' ? (
                        <>
                          {t.status !== 'doing' && (
                            <Button size="small" loading={acting[t.id] === 'doing'} onClick={() => act(t.id, 'doing')}>开始</Button>
                          )}
                          <Button size="small" type="primary" icon={<CheckOutlined />} loading={acting[t.id] === 'done'} onClick={() => act(t.id, 'done')}>完成</Button>
                          <Tooltip title="忽略(误判或不需要)">
                            <Button size="small" icon={<StopOutlined />} loading={acting[t.id] === 'ignored'} onClick={() => act(t.id, 'ignored')} />
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="移入历史,不再跟踪(可恢复)">
                          <Button size="small" icon={<StopOutlined />} loading={acting[t.id] === 'ignored'} onClick={() => act(t.id, 'ignored')}>不再跟踪</Button>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                ))}
                {items.length > SEC_SIZE && (
                  <div style={{ textAlign: 'right', padding: '8px 14px' }}>
                    <Pagination
                      size="small" current={pg} pageSize={SEC_SIZE} total={items.length}
                      showSizeChanger={false}
                      onChange={(p) => setSecPage((m) => ({ ...m, [sec.key]: p }))}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
      <TraceModal todo={tracing} onClose={() => setTracing(null)} />
    </div>
  );
}

// ===== 知识条目(卡片流) =====
function KnowledgePanel() {
  const [data, setData] = useState<{ total: number; items: WorkIntelKnowledge[]; categories: any[] }>({ total: 0, items: [], categories: [] });
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await workIntelApi.knowledge(page, 12, category));
    } catch {
      message.error('知识条目加载失败');
    } finally {
      setLoading(false);
    }
  }, [page, category]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <Space style={{ marginBottom: 14 }}>
        <Select
          allowClear
          placeholder="按大类过滤"
          style={{ width: 200 }}
          value={category}
          onChange={(v) => { setPage(1); setCategory(v); }}
          options={data.categories.map((c: any) => ({ value: c.category, label: `${c.category} (${c.count})` }))}
        />
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>刷新</Button>
      </Space>
      {data.total === 0 && !loading ? (
        <EmptyGuide title="还没有沉淀的知识条目" sub={PIPELINE_HINT} />
      ) : (
        <>
          <div className="wi-kn-grid">
            {data.items.map((k) => (
              <div key={k.id} className="wi-kn-card">
                <div className="wi-kn-head">
                  <span className="wi-chip wi-chip-cat">{k.category}</span>
                  {k.subCategory && <span className="wi-chip wi-chip-sub">{k.subCategory}</span>}
                  {k.vectorId && (
                    <Tooltip title="已入向量库,可被小灵语义检索">
                      <span className="wi-chip wi-chip-vec"><CheckCircleOutlined style={{ marginRight: 4 }} />已向量化</span>
                    </Tooltip>
                  )}
                </div>
                <h4 className="wi-kn-title">{k.title}</h4>
                <div className="wi-kn-summary">{k.summary}</div>
                <div className="wi-kn-foot">
                  {k.occurredAt && <span>发生 {fmtTime(k.occurredAt, 'YYYY-MM-DD HH:mm')}</span>}
                  <span>研判入库 {fmtTime(k.createdAt, 'YYYY-MM-DD HH:mm')}</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 18 }}>
            <Pagination current={page} pageSize={12} total={data.total} onChange={setPage} showTotal={(t) => `共 ${t} 条`} />
          </div>
        </>
      )}
    </div>
  );
}

/** 裸 wxid(采集器暂未补传备注名的真实群成员)展示成简短形式,可读不冒充人名 */
const isBareWxid = (s: string) => /^wxid_[a-z0-9_-]+$/i.test((s || '').trim());
const displayName = (name: string) => (isBareWxid(name) ? `wxid…${name.slice(-6)}` : name);

/** 联系人可搜索文本:名字/wxid/别名的原文 + 全拼 + 首字母(拼音搜索用),预计算一次 */
function buildSearchHay(c: WorkIntelContact): string {
  const names = [c.contactName, c.contactWxid, ...(c.aliases || [])].filter(Boolean) as string[];
  const parts: string[] = [];
  for (const n of names) {
    parts.push(n);
    try {
      parts.push(pinyin(n, { toneType: 'none', type: 'array' }).join(''));
      parts.push(pinyin(n, { pattern: 'first', toneType: 'none', type: 'array' }).join(''));
    } catch { /* 非中文忽略拼音 */ }
  }
  return parts.join(' ').toLowerCase();
}

/** 联系人档案编辑弹窗(联系人卡片用;与图谱内编辑同一后端接口,保存后两处同步刷新) */
function ContactEditModal({
  contact,
  companyOptions,
  onClose,
  onSaved,
}: {
  contact: WorkIntelContact | null;
  companyOptions: { value: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({ contactName: '', company: '', position: '', seniority: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!contact) return;
    const clean = (v?: string | null) => (v && v !== '未知' ? v : '');
    setForm({
      contactName: contact.contactName || '',
      company: clean(contact.company),
      position: clean(contact.position),
      seniority: clean(contact.seniority),
    });
  }, [contact]);

  const save = async () => {
    if (!contact) return;
    const name = form.contactName.trim();
    if (!name) { message.warning('名字不能为空'); return; }
    setSaving(true);
    try {
      const r = await workIntelApi.updateContact(contact.id, {
        contactName: name,
        company: form.company.trim(),
        position: form.position.trim(),
        seniority: form.seniority.trim(),
      });
      if (r?.ok) {
        message.success('已保存,AI 研判不会覆盖你的修改');
        onSaved();
        onClose();
      } else {
        message.error(r?.reason || '保存失败');
      }
    } catch {
      message.error('保存失败,请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={!!contact}
      onCancel={onClose}
      footer={null}
      width={440}
      className="wi-trace-modal"
      title={<span><EditOutlined style={{ marginRight: 8, color: '#22d3ee' }} />编辑联系人资料</span>}
    >
      {contact && (
        <div className="wi-graph-edit" style={{ marginTop: 6 }}>
          <div className="wi-graph-edit-row">
            <span className="wi-graph-edit-lbl">名字</span>
            <Input size="small" value={form.contactName} maxLength={100}
              onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))} placeholder="真实姓名/常用称呼" />
          </div>
          <div className="wi-graph-edit-row">
            <span className="wi-graph-edit-lbl">公司</span>
            <AutoComplete size="small" style={{ flex: 1 }} value={form.company} options={companyOptions}
              filterOption={(input, opt) => String(opt?.value || '').includes(input.trim())}
              onChange={(v) => setForm((f) => ({ ...f, company: String(v || '') }))}
              placeholder={companyOptions.length ? '下拉选已有公司或直接输入' : '直接输入公司名'} />
          </div>
          <div className="wi-graph-edit-row">
            <span className="wi-graph-edit-lbl">职务</span>
            <Input size="small" value={form.position} maxLength={100}
              onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))} placeholder="如:项目经理" />
          </div>
          <div className="wi-graph-edit-row">
            <span className="wi-graph-edit-lbl">级别</span>
            <Select size="small" style={{ flex: 1 }} value={form.seniority || undefined} allowClear
              onChange={(v) => setForm((f) => ({ ...f, seniority: String(v || '') }))}
              placeholder="高层 / 中层 / 基层"
              options={['高层', '中层', '基层'].map((v) => ({ value: v, label: v }))} />
          </div>
          <div className="wi-graph-edit-actions">
            <Button size="small" onClick={onClose}>取消</Button>
            <Button size="small" type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>保存</Button>
          </div>
          <div className="wi-graph-edit-hint">手工修改后 AI 研判永不覆盖;此处与关系图谱内的编辑共用同一档案</div>
        </div>
      )}
    </Modal>
  );
}

// ===== 联系人(卡片) =====
// version:父级联系人版本号(任一处编辑后 +1)→ 触发本面板重载,与图谱双向同步
/** 卡片"已提炼画像"判定:领导取汇报画像一句话;普通人取同事画像;老 personality 兜底 */
function hasRichPersona(c: WorkIntelContact): boolean {
  if (c.isLeader && (c.audienceProfile?.oneLiner || '').trim()) return true;
  if ((c.personaProfile?.oneLiner || '').trim()) return true;
  return (c.realMsgCount ?? 0) >= PERSONA_MIN_MSGS && !!(c.personality || '').trim();
}

function ContactsPanel({ version, onChanged }: { version: number; onChanged: () => void }) {
  const [all, setAll] = useState<WorkIntelContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<WorkIntelContact | null>(null);
  const [viewing, setViewing] = useState<WorkIntelContact | null>(null);
  // 并发研判:每人独立 loading(此前单值状态,点第二个人会顶掉第一个人的等待态)
  const [genningIds, setGenningIds] = useState<Set<string>>(new Set());
  const pageSize = 12;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await workIntelApi.contacts(1, 200);
      setAll(d?.items || []);
    } catch {
      message.error('联系人加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, version]);

  /** 研判/重新研判某人的同事工作画像(手动触发,LLM 按其最近聊天记录提炼) */
  const genPersona = async (c: WorkIntelContact) => {
    setGenningIds((prev) => new Set(prev).add(c.id));
    try {
      const r = await workIntelApi.generatePersona(c.id);
      if (r?.ok) {
        message.success('工作画像研判完成');
        setAll((list) => list.map((x) => (x.id === c.id ? { ...x, personaProfile: r.profile } : x)));
        setViewing((v) => (v && v.id === c.id ? { ...v, personaProfile: r.profile } : v));
      } else if (r?.reason === 'too_few_messages') {
        message.warning(`数据不足:仅 ${r.basedOnMsgs ?? 0} 条真实消息(满 ${PERSONA_MIN_MSGS} 条才能研判)`);
      } else {
        message.error(`研判失败:${r?.reason || '未知错误'}`);
      }
    } catch {
      message.error('研判失败,请稍后重试');
    } finally {
      setGenningIds((prev) => { const n = new Set(prev); n.delete(c.id); return n; });
    }
  };

  // 已知公司清单(编辑弹窗下拉)
  const companyOptions = useMemo(() => {
    const set = new Set<string>();
    all.forEach((c) => { const v = (c.company || '').trim(); if (v && v !== '未知') set.add(v); });
    return Array.from(set).map((v) => ({ value: v }));
  }, [all]);

  // 搜索索引:名字/wxid/别名 原文 + 全拼 + 首字母,随 all 预计算
  const index = useMemo(() => all.map((c) => ({ c, hay: buildSearchHay(c) })), [all]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return index.filter((x) => x.hay.includes(q)).map((x) => x.c);
  }, [index, all, query]);
  useEffect(() => { setPage(1); }, [query]);

  // 重要度排序:领导优先,其次有画像的,让"重要的/能提炼出来的"一眼看到
  const sorted = useMemo(() => {
    const score = (c: WorkIntelContact) => (c.isLeader ? 2 : 0) + (hasRichPersona(c) ? 1 : 0);
    return [...filtered].sort((a, b) => score(b) - score(a));
  }, [filtered]);
  const paged = sorted.slice((page - 1) * pageSize, page * pageSize);
  const richCount = useMemo(() => all.filter(hasRichPersona).length, [all]);

  return (
    <div>
      <Space style={{ marginBottom: 14 }} wrap>
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: '#7f95bd' }} />}
          placeholder="搜索名字 / 拼音 / 首字母(如 cjj、柴)"
          style={{ width: 280 }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>刷新</Button>
        <span style={{ color: '#93aed6', fontSize: 13 }}>
          共 {all.length} 人 · <b style={{ color: '#5eead4' }}>{richCount}</b> 人已提炼画像
          {query && ` · 命中 ${filtered.length} 人`}
        </span>
      </Space>
      {all.length === 0 && !loading ? (
        <EmptyGuide title="还没有联系人档案" sub={PIPELINE_HINT} />
      ) : filtered.length === 0 ? (
        <Empty description={<span style={{ color: '#93aed6' }}>没有匹配「{query}」的联系人</span>} />
      ) : (
        <>
          <div className="wi-ct-grid">
            {paged.map((c) => {
              const rawName = c.contactName || c.contactWxid;
              const name = displayName(rawName);
              const bare = isBareWxid(rawName);
              const rich = hasRichPersona(c);
              const isLeader = !!c.isLeader;
              const leaderLine = (c.audienceProfile?.oneLiner || '').trim();
              const enough = (c.realMsgCount ?? 0) >= PERSONA_MIN_MSGS;
              return (
                <div key={c.id} className={`wi-ct-card${rich ? ' wi-ct-card-rich' : ''}${isLeader ? ' wi-ct-card-leader' : ''}`}>
                  <button className="wi-ct-edit-btn" onClick={() => setEditing(c)} title="编辑资料">
                    <EditOutlined />
                  </button>
                  <div className="wi-ct-head">
                    <div className={`wi-ct-avatar${isLeader ? ' wi-ct-avatar-leader' : ''}`}>
                      {isLeader ? <CrownOutlined /> : bare ? '?' : (name || '?').slice(0, 1)}
                    </div>
                    <div>
                      <div className="wi-ct-name" style={bare ? { color: 'rgba(203,213,229,.85)' } : undefined}>
                        {name}
                        {isLeader && <span className="wi-ct-leader-chip">👑 领导</span>}
                      </div>
                      {c.role && <div className="wi-ct-role">{c.role}</div>}
                    </div>
                  </div>
                  <div className="wi-ct-org">
                    {([
                      ['🏢', c.company],
                      ['💼', c.position],
                      ['📶', c.seniority],
                    ] as const).map(([icon, v], i) =>
                      v && v !== '未知' ? (
                        <span key={i} className="wi-chip wi-chip-sub">{icon} {v}</span>
                      ) : (
                        <span key={i} className="wi-chip wi-chip-unknown">{icon} 未知</span>
                      ),
                    )}
                  </div>
                  {(c.sourceGroups?.length ?? 0) > 0 && (
                    <div className="wi-ct-groups">来自群:{c.sourceGroups!.join('、')}</div>
                  )}
                  {/* 画像区分级:
                      领导 → 直接展示"领导信息"里汇报画像的一句话(单一事实源,不再用批次累积的 personality,消除两处不一致);
                      普通人 → 卡片不铺开维度,给「画像详情 / 研判画像」入口,详情弹窗看工作方式/特点/性格等;
                      数据不足 → 弱化提示 */}
                  {isLeader && leaderLine ? (
                    <div className="wi-ct-persona wi-ct-persona-leader">
                      <span className="wi-ct-persona-tag wi-ct-persona-tag-leader">👑 领导画像</span>
                      {leaderLine}
                    </div>
                  ) : isLeader && enough ? (
                    <div className="wi-ct-persona wi-ct-persona-insufficient">
                      还没生成汇报画像 · 去「领导信息」页签点「生成画像」
                    </div>
                  ) : !enough ? (
                    <div className="wi-ct-persona wi-ct-persona-insufficient">
                      数据不足 · 仅 {c.realMsgCount ?? 0} 条消息(满 {PERSONA_MIN_MSGS} 条后可研判画像)
                    </div>
                  ) : (
                    <>
                      {(() => {
                        const line = (c.personaProfile?.oneLiner || '').trim() || (c.personality || '').trim();
                        return line ? (
                          <div className="wi-ct-persona wi-ct-persona-rich">
                            <span className="wi-ct-persona-tag">✨ 工作画像</span>
                            {line}
                          </div>
                        ) : null;
                      })()}
                      <div className="wi-ct-persona-entry">
                        {c.personaProfile ? (
                          <>
                            <Button size="small" icon={<FileSearchOutlined />} onClick={() => setViewing(c)}>画像详情</Button>
                            <Button size="small" icon={<RedoOutlined />} loading={genningIds.has(c.id)} onClick={() => genPersona(c)}>重新研判</Button>
                          </>
                        ) : (
                          <Button size="small" type="primary" ghost icon={<ThunderboltOutlined />} loading={genningIds.has(c.id)} onClick={() => genPersona(c)}>
                            研判画像
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                  <div className="wi-ct-foot">
                    <span>消息 {c.realMsgCount ?? 0} 条</span>
                    <span>研判 {c.msgCount} 次</span>
                    {c.lastInteractAt && <span>最近 {fmtTime(c.lastInteractAt)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ textAlign: 'center', marginTop: 18 }}>
            <Pagination current={page} pageSize={pageSize} total={filtered.length} onChange={setPage} showTotal={(t) => `共 ${t} 人`} />
          </div>
        </>
      )}
      <ContactEditModal
        contact={editing}
        companyOptions={companyOptions}
        onClose={() => setEditing(null)}
        onSaved={() => { load(); onChanged(); }}
      />
      {/* 工作画像详情弹窗:维度只在这里铺开,卡片上不显示 */}
      <Modal
        open={!!viewing}
        onCancel={() => setViewing(null)}
        footer={null}
        width={620}
        className="df-readable-modal"
        title={viewing ? `工作画像 · ${displayName(viewing.contactName || viewing.contactWxid)}` : ''}
      >
        {viewing?.personaProfile && (
          <div className="wi-persona-detail">
            <div className="wi-persona-oneliner">“{viewing.personaProfile.oneLiner}”</div>
            {([
              ['🛠️', '工作方式', viewing.personaProfile.workStyle],
              ['🎯', '工作特点', viewing.personaProfile.workTraits],
              ['🧭', '工作性格', viewing.personaProfile.workCharacter],
              ['💬', '沟通风格', viewing.personaProfile.commStyle],
              ['🤝', '协作建议', viewing.personaProfile.advice],
            ] as const).map(([icon, label, v]) => (
              <div key={label} className="wi-persona-dim">
                <div className="wi-persona-dim-label">{icon} {label}</div>
                <div className="wi-persona-dim-val">{v || '暂不明确'}</div>
              </div>
            ))}
            <div className="wi-persona-meta">
              <span>
                基于 {viewing.personaProfile.basedOnMsgs ?? '-'} 条真实消息 ·
                生成于 {viewing.personaProfile.generatedAt ? fmtTime(viewing.personaProfile.generatedAt) : '-'}
              </span>
              <Button
                size="small"
                icon={<RedoOutlined />}
                loading={genningIds.has(viewing.id)}
                onClick={() => genPersona(viewing)}
              >
                重新研判
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ===== 人际关系 =====
// version/onChanged:与联系人档案面板双向同步(任一处编辑联系人后互相刷新)
function RelationsPanel({ version, onChanged }: { version: number; onChanged: () => void }) {
  const [data, setData] = useState<{ total: number; items: WorkIntelRelation[] }>({ total: 0, items: [] });
  const [contacts, setContacts] = useState<WorkIntelContact[]>([]);
  const [selfIds, setSelfIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<'graph' | 'list'>('graph');
  const [relPage, setRelPage] = useState(1);
  // 图谱全屏子页面(独立浮层,支持平移缩放,画布可向四周延伸)
  const [fullscreen, setFullscreen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rel, ct] = await Promise.all([
        workIntelApi.relations(1, 200),
        workIntelApi.contacts(1, 200),
      ]);
      setData(rel);
      setContacts(ct?.items || []);
      // 本人标识集合(后端从 raw 的 is_self 计算,通用机制):图谱据此把本人端点合并到"我"
      setSelfIds(rel?.meta?.selfIds || ct?.meta?.selfIds || []);
    } catch {
      message.error('人际关系加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, version]);
  // 图谱内编辑联系人 → 本面板重载 + 通知联系人档案面板同步
  const reloadAndSync = useCallback(async () => { await load(); onChanged(); }, [load, onChanged]);

  // 全屏时按 Esc 退出,并锁住底层页面滚动
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFullscreen(false); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [fullscreen]);

  const columns = [
    { title: '甲方', dataIndex: 'fromWxid', width: 170 },
    { title: '乙方', dataIndex: 'toWxid', width: 170 },
    { title: '关系', dataIndex: 'relationType', width: 120, render: (v: string) => (v ? <span className="wi-chip wi-chip-sub">{v}</span> : '-') },
    { title: '权重', dataIndex: 'weight', width: 80 },
    { title: '证据(AI 摘录)', dataIndex: 'evidence', render: (v: string) => v || '-' },
  ];

  const empty = data.total === 0 && contacts.length === 0;

  return (
    <div>
      <Space style={{ marginBottom: 14 }}>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>刷新</Button>
        <div className="wi-view-toggle">
          <button type="button" className={view === 'graph' ? 'on' : ''} onClick={() => setView('graph')}>
            <ShareAltOutlined /> 图谱
          </button>
          <button type="button" className={view === 'list' ? 'on' : ''} onClick={() => setView('list')}>
            <UnorderedListOutlined /> 列表
          </button>
        </div>
        {view === 'graph' && !empty && (
          <Button icon={<FullscreenOutlined />} onClick={() => setFullscreen(true)}>全屏图谱</Button>
        )}
      </Space>
      {empty && !loading ? (
        <EmptyGuide title="还没有关系线索" sub={PIPELINE_HINT} />
      ) : view === 'graph' ? (
        <Suspense
          fallback={(
            <div className="wi-graph-fallback"><Spin /> 图谱渲染引擎加载中…</div>
          )}
        >
          <RelationGraph relations={data.items} contacts={contacts} selfIds={selfIds} onReload={reloadAndSync} />
        </Suspense>
      ) : data.total === 0 && !loading ? (
        <EmptyGuide title="还没有关系线索" sub={PIPELINE_HINT} />
      ) : (
        <Table
          rowKey="id" size="middle" loading={loading} columns={columns as any} dataSource={data.items}
          pagination={{ current: relPage, pageSize: 15, onChange: setRelPage, showTotal: (t) => `共 ${t} 条关系`, showSizeChanger: false }}
        />
      )}

      {/* 全屏图谱子页面:portal 直挂 document.body ——
          布局容器带 transform/filter 时 position:fixed 会被"囚禁"在内容区(全屏跟 tab 一样大的根因),
          portal 脱离所有祖先,inset:0 才是真视口全屏 */}
      {fullscreen &&
        createPortal(
          <div className="wi-graph-fs">
            <div className="wi-graph-fs-head">
              <div className="wi-graph-fs-title">
                <ShareAltOutlined /> 人际关系图谱
                <span className="wi-graph-fs-hint">按住空白处拖动平移 · 滚轮缩放 · 点击人物高亮TA到我的关系链 · Esc 退出</span>
              </div>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>刷新数据</Button>
                <Button type="primary" icon={<FullscreenExitOutlined />} onClick={() => setFullscreen(false)}>退出全屏</Button>
              </Space>
            </div>
            <div className="wi-graph-fs-body">
              <Suspense
                fallback={(
                  <div className="wi-graph-fallback"><Spin /> 图谱渲染引擎加载中…</div>
                )}
              >
                <RelationGraph relations={data.items} contacts={contacts} selfIds={selfIds} fullscreen onReload={reloadAndSync} />
              </Suspense>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

// ===== 领导信息(汇报对象画像) =====
// 识别疑似领导/业主,提炼其思维/关注点/业务倾向/文档偏好,存入知识库;
// 后续智坊写文档/PPT 时可选汇报对象,取此画像定制内容,让对方更满意。
function LeadersPanel({ version }: { version: number }) {
  const [items, setItems] = useState<WorkIntelContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [gen, setGen] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await workIntelApi.leaders();
      setItems(d?.items || []);
    } catch {
      message.error('领导信息加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, version]);

  const generate = async (c: WorkIntelContact) => {
    setGen((m) => ({ ...m, [c.id]: true }));
    try {
      const r = await workIntelApi.genLeaderProfile(c.id);
      if (r?.ok) {
        message.success('汇报画像已生成并存入知识库');
        setItems((list) => list.map((x) => (x.id === c.id ? { ...x, audienceProfile: r.profile } : x)));
      } else if (r?.reason === 'too_few_messages') {
        message.warning(`聊天记录太少(仅 ${r.basedOnMsgs ?? 0} 条),不足以提炼可靠画像`);
      } else {
        message.error(`生成失败:${r?.reason || '未知错误'}`);
      }
    } catch {
      message.error('生成失败,请稍后重试');
    } finally {
      setGen((m) => { const { [c.id]: _, ...rest } = m; return rest; });
    }
  };

  // 从领导信息移除:仅标记不再当领导,联系人仍保留在情报中
  const remove = async (c: WorkIntelContact) => {
    try {
      const r = await workIntelApi.excludeLeader(c.id);
      if (r?.ok) {
        message.success('已移出领导信息(联系人仍保留)');
        setItems((list) => list.filter((x) => x.id !== c.id));
      } else {
        message.error(r?.reason || '移除失败');
      }
    } catch {
      message.error('移除失败,请稍后重试');
    }
  };

  const dims: Array<{ key: keyof AudienceProfile; label: string; icon: string }> = [
    { key: 'thinking', label: '思维方式', icon: '🧠' },
    { key: 'focus', label: '关注重点', icon: '🎯' },
    { key: 'bizTendency', label: '业务倾向', icon: '📈' },
    { key: 'commStyle', label: '沟通风格', icon: '💬' },
    { key: 'docPreference', label: '文档偏好', icon: '📄' },
    { key: 'advice', label: '写作建议', icon: '✍️' },
  ];

  return (
    <div>
      <div className="wi-leader-intro">
        <CrownOutlined style={{ color: '#fbbf24', marginRight: 8 }} />
        专门提炼<b>领导 / 业主 / 甲方决策人</b>的思维、关注点、业务倾向与看文档喜好,存入知识库。
        以后在智坊写文档 / PPT 时可<b>选定汇报对象</b>,自动按这份画像定制内容,让材料更合对方口味。
      </div>
      <Space style={{ marginBottom: 14 }}>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>刷新</Button>
        <span style={{ color: '#93aed6', fontSize: 13 }}>共识别 {items.length} 位疑似领导/业主(依据角色、级别、上级关系)</span>
      </Space>
      {items.length === 0 && !loading ? (
        <EmptyGuide
          title="还没有识别到领导/业主"
          sub={<>系统按<b>角色关键词、级别=高层、上级关系</b>自动识别。可在联系人档案或图谱里把某人级别改成「高层」,他就会出现在这里。</>}
        />
      ) : (
        <div className="wi-leader-grid">
          {items.map((c) => {
            const name = displayName(c.contactName || c.contactWxid);
            const p = c.audienceProfile;
            const busy = !!gen[c.id];
            const few = (c.realMsgCount ?? 0) < PERSONA_MIN_MSGS;
            return (
              <div key={c.id} className="wi-leader-card">
                <div className="wi-leader-head">
                  <div className="wi-leader-avatar"><CrownOutlined /></div>
                  <div className="wi-leader-id">
                    <div className="wi-leader-name">{name}</div>
                    <div className="wi-leader-sub">
                      {[c.role, c.company && c.company !== '未知' ? c.company : null, c.position && c.position !== '未知' ? c.position : null]
                        .filter(Boolean).join(' · ') || '角色待完善'}
                      <span className="wi-leader-msgs">消息 {c.realMsgCount ?? 0} 条</span>
                    </div>
                  </div>
                  <Space size={6}>
                    <Button
                      size="small"
                      type={p ? 'default' : 'primary'}
                      icon={<ThunderboltOutlined />}
                      loading={busy}
                      onClick={() => generate(c)}
                    >
                      {p ? '更新画像' : '生成画像'}
                    </Button>
                    <Popconfirm
                      title="不是领导?"
                      description="移出领导信息(联系人仍保留在情报中,不会删除)"
                      okText="确认移除"
                      cancelText="取消"
                      onConfirm={() => remove(c)}
                    >
                      <Tooltip title="他不是领导 → 移出领导信息">
                        <Button size="small" icon={<StopOutlined />} />
                      </Tooltip>
                    </Popconfirm>
                  </Space>
                </div>
                {p ? (
                  <div className="wi-leader-profile">
                    {p.oneLiner && <div className="wi-leader-oneliner">"{p.oneLiner}"</div>}
                    <div className="wi-leader-dims">
                      {dims.map((d) => (
                        p[d.key] ? (
                          <div key={d.key} className="wi-leader-dim">
                            <span className="wi-leader-dim-lbl">{d.icon} {d.label}</span>
                            <span className="wi-leader-dim-val">{p[d.key] as string}</span>
                          </div>
                        ) : null
                      ))}
                    </div>
                    <div className="wi-leader-foot">
                      基于 {p.basedOnMsgs ?? 0} 条消息 · {fmtTime(p.generatedAt, 'YYYY-MM-DD HH:mm')} 生成 · 已入知识库
                    </div>
                  </div>
                ) : (
                  <div className="wi-leader-empty">
                    {few
                      ? `聊天记录较少(${c.realMsgCount ?? 0} 条),生成的画像可能不够准。攒够更多互动后再生成更佳。`
                      : '点「生成画像」由 AI 按聊天记录提炼汇报画像并存入知识库。'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ===== 我的画像(用户本人) =====
function SelfProfilePanel() {
  const [profile, setProfile] = useState<import('../../services/workIntelApi').SelfPersona | null>(null);
  const [selfMsgCount, setSelfMsgCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [gen, setGen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await workIntelApi.selfProfile();
      setProfile(r.profile);
      setSelfMsgCount(r.selfMsgCount || 0);
    } catch {
      message.error('我的画像加载失败');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGen(true);
    try {
      const r = await workIntelApi.generateSelfProfile();
      if (r?.ok) {
        message.success(profile ? '已按最新发言增量更新我的画像' : '我的画像研判完成');
        setProfile(r.profile);
      } else if (r?.reason === 'too_few_messages') {
        message.warning(`你自己的发言太少(仅 ${r.basedOnMsgs ?? 0} 条),满 ${PERSONA_MIN_MSGS} 条才能研判`);
      } else {
        message.error(`研判失败:${r?.reason || '未知错误'}`);
      }
    } catch {
      message.error('研判失败,请稍后重试');
    } finally {
      setGen(false);
    }
  };

  const dims: Array<{ key: keyof import('../../services/workIntelApi').SelfPersona; label: string; icon: string }> = [
    { key: 'workStyle', label: '工作方式', icon: '🛠️' },
    { key: 'strengths', label: '专长优势', icon: '⭐' },
    { key: 'workCharacter', label: '工作性格', icon: '🧭' },
    { key: 'commStyle', label: '沟通风格', icon: '💬' },
    { key: 'thinking', label: '决策思维', icon: '🧠' },
    { key: 'growth', label: '可提升点', icon: '🌱' },
  ];

  return (
    <div>
      <div className="wi-leader-intro">
        <UserOutlined style={{ color: '#7dd3fc', marginRight: 8 }} />
        基于<b>你本人在各个聊天里的发言</b>提炼你自己的职场画像——工作方式、专长、性格、沟通与决策思维。
        这是给你的自我认知镜子,也让智坊更懂你、以你的风格协助你。<b>重新研判为增量更新</b>,历史沉淀的特征不会被抹掉。
      </div>
      <Space style={{ marginBottom: 14 }} wrap>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>刷新</Button>
        <Button
          type="primary"
          icon={profile ? <RedoOutlined /> : <ThunderboltOutlined />}
          loading={gen}
          onClick={generate}
        >
          {profile ? '重新研判(增量更新)' : '研判我的画像'}
        </Button>
        <span style={{ color: '#93aed6', fontSize: 13 }}>你的发言累计 <b style={{ color: '#5eead4' }}>{selfMsgCount}</b> 条</span>
      </Space>

      {loading && !profile ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : !profile ? (
        <EmptyGuide
          title="还没有你的画像"
          sub={
            selfMsgCount < PERSONA_MIN_MSGS
              ? <>你自己的发言目前 <b>{selfMsgCount}</b> 条,满 <b>{PERSONA_MIN_MSGS}</b> 条即可研判。保持采集器运行、多聊几句就够了。</>
              : <>点上面「研判我的画像」,让智坊根据你的发言画一份你的职场画像。</>
          }
        />
      ) : (
        <div className="wi-self-card">
          <div className="wi-self-oneliner">“{profile.oneLiner}”</div>
          <div className="wi-self-dims">
            {dims.map((d) => (
              <div key={d.key} className={`wi-self-dim${d.key === 'growth' ? ' wi-self-dim-growth' : ''}`}>
                <div className="wi-self-dim-label">{d.icon} {d.label}</div>
                <div className="wi-self-dim-val">{profile[d.key] || '暂不明确'}</div>
              </div>
            ))}
          </div>
          <div className="wi-self-meta">
            基于 {profile.basedOnMsgs ?? '-'} 条本人发言
            {profile.rounds ? ` · 已增量融合 ${profile.rounds} 轮` : ''}
            {profile.generatedAt ? ` · 更新于 ${fmtTime(profile.generatedAt)}` : ''}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== 批次日志 =====
function BatchesPanel() {
  const [data, setData] = useState<{ total: number; items: WorkIntelBatch[] }>({ total: 0, items: [] });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await workIntelApi.batches(page, 20));
    } catch {
      message.error('批次日志加载失败');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const retry = async (id: string) => {
    const r = await workIntelApi.retryBatch(id);
    if (r?.ok) {
      message.success('已重新排入研判,稍后刷新查看');
      setTimeout(load, 1500);
    } else {
      message.error(r?.reason || '重试失败');
    }
  };

  const statusPill = (s: string) => {
    const map: Record<string, { cls: string; text: string }> = {
      done: { cls: 'wi-batch-done', text: '✓ 完成' },
      failed: { cls: 'wi-batch-failed', text: '✕ 失败' },
      refining: { cls: 'wi-batch-refining', text: '⟳ 研判中' },
      buffered: { cls: 'wi-batch-buffered', text: '已攒批' },
    };
    const m = map[s] || { cls: 'wi-batch-buffered', text: s };
    return <span className={`wi-batch-pill ${m.cls}`}>{m.text}</span>;
  };

  const columns = [
    { title: '批次', dataIndex: 'id', width: 100, render: (v: string) => <Tooltip title={v}><span style={{ fontFamily: 'monospace' }}>{v.slice(0, 8)}</span></Tooltip> },
    { title: '状态', dataIndex: 'status', width: 110, render: statusPill },
    { title: '消息数', dataIndex: 'msgCount', width: 80 },
    { title: '触发', dataIndex: 'triggerReason', width: 100 },
    { title: '模型', dataIndex: 'modelUsed', width: 150, render: (v: string) => v || '-' },
    { title: 'Tokens 入/出', width: 130, render: (_: any, r: WorkIntelBatch) => `${r.promptTokens} / ${r.completionTokens}` },
    { title: '开始', dataIndex: 'startedAt', width: 115, render: (v: string) => fmtTime(v) },
    {
      title: '错误 / 操作',
      render: (_: any, r: WorkIntelBatch) => (
        <Space>
          {r.error && (
            <Tooltip title={r.error}>
              <span style={{ color: '#fca5a5', maxWidth: 260, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>{r.error}</span>
            </Tooltip>
          )}
          {r.status === 'failed' && (
            <Button size="small" icon={<RedoOutlined />} onClick={() => retry(r.id)}>重试</Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ marginBottom: 14 }}>刷新</Button>
      {data.total === 0 && !loading ? (
        <EmptyGuide title="还没有研判批次" sub={PIPELINE_HINT} />
      ) : (
        <Table
          rowKey="id" size="middle" loading={loading} columns={columns as any} dataSource={data.items}
          pagination={{ current: page, pageSize: 20, total: data.total, onChange: setPage, showTotal: (t) => `共 ${t} 批` }}
        />
      )}
    </div>
  );
}

// ===== 主页面 =====
export default function WorkIntelPage() {
  const [overview, setOverview] = useState<any>(null);
  // 联系人版本号:联系人档案面板 / 关系图谱 任一处编辑联系人后 +1,
  // 两个面板都 watch 它 → 一处改动,两处同步刷新
  const [contactVersion, setContactVersion] = useState(0);
  const bumpContacts = useCallback(() => setContactVersion((v) => v + 1), []);

  const loadOverview = useCallback(async () => {
    try {
      setOverview(await workIntelApi.overview());
    } catch { /* 顶部计数失败不阻塞页面 */ }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const stats = [
    { cls: 'wi-stat-cyan', icon: <BulbOutlined />, val: overview?.knowledge, lbl: '知识条目' },
    { cls: 'wi-stat-amber', icon: <ThunderboltOutlined />, val: overview?.todoOpen, lbl: '在办事项' },
    { cls: 'wi-stat-green', icon: <CheckCircleOutlined />, val: overview?.todoDone, lbl: '已办结' },
    { cls: 'wi-stat-violet', icon: <TeamOutlined />, val: overview?.contacts, lbl: '联系人档案' },
    { cls: 'wi-stat-blue', icon: <ApartmentOutlined />, val: overview?.relations, lbl: '关系线索' },
    { cls: 'wi-stat-slate', icon: <DatabaseOutlined />, val: overview?.batches, lbl: '研判批次' },
  ];

  return (
    <div className="wi-page">
      <div className="wi-hero">
        <h2 className="wi-hero-title"><RadarChartOutlined /> 个人工作情报</h2>
        <div className="wi-hero-sub">
          微信消息 → 攒批缓冲 → AI 多视角研判 → 待办 / 知识 / 人脉 结构化沉淀 · 数据仅本人可见
        </div>
        <div className="wi-hero-actions">
          <Button ghost icon={<ReloadOutlined />} onClick={loadOverview}>刷新总览</Button>
        </div>
      </div>

      <div className="wi-stats">
        {stats.map((s) => (
          <div key={s.lbl} className={`wi-stat ${s.cls}`}>
            <div className="wi-stat-icon">{s.icon}</div>
            <div className="wi-stat-val">{s.val ?? '—'}</div>
            <div className="wi-stat-lbl">{s.lbl}</div>
          </div>
        ))}
      </div>

      <div className="wi-body">
        <Tabs
          defaultActiveKey="radar"
          items={[
            { key: 'self', label: '🙋 我的画像', children: <SelfProfilePanel /> },
            { key: 'radar', label: '⏰ 待办雷达', children: <RadarPanel onChanged={loadOverview} /> },
            { key: 'knowledge', label: '💡 知识条目', children: <KnowledgePanel /> },
            { key: 'contacts', label: '👥 联系人档案', children: <ContactsPanel version={contactVersion} onChanged={bumpContacts} /> },
            { key: 'leaders', label: '👑 领导信息', children: <LeadersPanel version={contactVersion} /> },
            { key: 'relations', label: '🕸 人际关系', children: <RelationsPanel version={contactVersion} onChanged={bumpContacts} /> },
            { key: 'batches', label: '📦 研判批次', children: <BatchesPanel /> },
          ]}
        />
      </div>
    </div>
  );
}
